import { resolveRef, type ColorData, type Ramp } from "./tokens";

// Source of truth for all colors. The CSS variables are generated from this
// file at build time (vite/colorTokens.ts).

export const scales = {
  pine: {
    50: "#f1f9f7",
    100: "#e1edeb",
    200: "#cadcd8",
    300: "#b0c5c2",
    400: "#88a9a3",
    500: "#618c85",
    600: "#42786f",
    700: "#26635a",
    800: "#0f5046",
    900: "#00323c",
    950: "#07232a",
  },
  leaf: {
    50: "#f0fff5",
    100: "#e4fbe6",
    200: "#c8f4cc",
    300: "#a5e8ab",
    400: "#78d585",
    500: "#50be64",
    600: "#2d9d47",
    650: "#178636",
    700: "#0e7c2f",
    800: "#025c1f",
    900: "#004012",
    950: "#002808",
  },
  orange: {
    50: "#fef5ed",
    100: "#fff1e5",
    200: "#fee5cf",
    300: "#fed3aa",
    400: "#ffb66d",
    500: "#fa9600",
    600: "#ca7906",
    700: "#9d5d03",
    800: "#744302",
    900: "#4f2c00",
    950: "#301900",
  },
  sage: {
    50: "#f2f7f6",
    100: "#e6eeec",
    200: "#d1dfdc",
    300: "#b7ccc8",
    400: "#99b2ad",
    500: "#7e9692",
    600: "#667b77",
    700: "#50605d",
    800: "#3d4745",
    900: "#2b3231",
    950: "#1c201f",
  },
  red: {
    50: "#fff4f2",
    100: "#ffe9e5",
    200: "#fed5ce",
    300: "#feb9ad",
    400: "#fe8f7f",
    500: "#fe5745",
    600: "#e82417",
    700: "#bb0801",
    800: "#8d0400",
    900: "#640200",
    950: "#420100",
  },
  blue: {
    50: "#f2f7ff",
    100: "#e1eefe",
    200: "#c4deff",
    300: "#9ec9ff",
    400: "#6aadfe",
    500: "#2e8ff8",
    600: "#0672d5",
    700: "#0159aa",
    800: "#024382",
    900: "#002f5f",
    950: "#001f42",
  },
} as const;

type Scale = keyof typeof scales;
type ColorRef =
  | { [F in Scale]: `${F}.${keyof (typeof scales)[F] & number}` }[Scale]
  | `#${string}`;

const roleRefs = {
  surfaceDark: "pine.800",
  onSurfaceDark: "#ffffff",
  surface: "#ffffff",
  surfaceMuted: "leaf.50",
  surfaceSelected: "pine.100",
  surfacePage: "sage.50",
  shadow: "#00000026",
  backdrop: "#000000",
  border: "pine.200",
  borderStrong: "pine.500",
  text: "pine.900",
  textMuted: "pine.700",
  primary: "leaf.650",
  primaryHover: "leaf.800",
  onPrimary: "#ffffff",
  link: "pine.800",
  linkHover: "pine.900",
  selected: "leaf.500",
  mapDefault: "pine.300",
  mapFill: "leaf.200",
  mapHandle: "pine.500",
  mapStroke: "#ffffff",
  mapOutline: "#0f5046bf",
  logo: "leaf.500",
  logoText: "pine.800",
  logoAlternative: "orange.500",
  controlChecked: "leaf.650",
  focus: "leaf.600",
  danger: "red.700",
  dangerSurface: "red.50",
  warningIcon: "orange.600",
  warningSurface: "orange.50",
  warningFill: "orange.500",
  onWarning: "pine.900",
  info: "blue.700",
  infoSurface: "blue.50",
  success: "leaf.700",
  successSurface: "leaf.50",
} satisfies Record<string, ColorRef>;

const onDarkRefs = {
  text: "#ffffff",
  textMuted: "pine.200",
  surfaceSelected: "pine.700",
  logoText: "#ffffff",
  primary: "#ffffff",
  primaryHover: "leaf.50",
  onPrimary: "pine.800",
  link: "#ffffff",
  linkHover: "leaf.200",
  border: "pine.600",
  borderStrong: "pine.300",
  focus: "leaf.400",
} satisfies Partial<Record<keyof typeof roleRefs, ColorRef>>;

// Chart and map colors. A value is a hex color or the name of another entry.
const ramps = {
  "graph-0": "#cc1b1f",
  "graph-1": "#da5539",
  "graph-2": "#e98d53",
  "graph-3": "#f1b870",
  "graph-4": "#bfe189",
  "graph-5": "#9ed269",
  "graph-6": "#65b757",
  "graph-7": "#329f4b",
  "apple-map-0": "graph-0",
  "apple-map-1": "graph-1",
  "apple-map-2": "graph-2",
  "apple-map-3": "graph-3",
  "apple-map-4": "graph-4",
  "apple-map-5": "graph-5",
  "apple-map-6": "graph-6",
  "apple-map-7": "graph-7",
  "apple-calibration-map-0": "#d7191c",
  "apple-calibration-map-1": "#fdae61",
  "apple-calibration-map-2": {
    value: "#ffffc0",
    note: "mostly used on the map, not enough contrast for general display",
  },
  "apple-calibration-map-2-alternative": {
    value: "#f6e04d",
    note: "alternative for general display",
  },
  "apple-calibration-map-3": "#a6d96a",
  "apple-calibration-map-4": "#1a9641",
  "apple-selected-fill": "#7890b0",
  "blossom-map-0": "graph-0",
  "blossom-map-1": "graph-1",
  "blossom-map-2": "graph-2",
  "blossom-map-3": "graph-3",
  "blossom-map-4": "graph-4",
  "blossom-map-5": "graph-5",
  "blossom-map-6": "graph-6",
  "blossom-map-7": "graph-7",
  "vigour-map-0": "graph-0",
  "vigour-map-1": "graph-1",
  "vigour-map-2": "graph-2",
  "vigour-map-3": "graph-3",
  "vigour-map-4": "graph-4",
  "vigour-map-5": "graph-5",
  "vigour-map-6": "graph-6",
  "vigour-map-7": "graph-7",
} satisfies Record<string, Ramp>;

export const colorData: ColorData = {
  scales,
  roles: roleRefs,
  onDark: onDarkRefs,
  ramps,
};

function resolveAll<T extends Record<string, string>>(refs: T) {
  return Object.fromEntries(
    Object.entries(refs).map(([name, ref]) => [name, resolveRef(scales, ref)]),
  ) as { [K in keyof T]: string };
}

export const colors = resolveAll(roleRefs);

export type ColorToken = keyof typeof colors;

// Roles that differ on the dark page background (`.on-dark` class)
export const onDark = resolveAll(onDarkRefs);
